import React, { forwardRef } from 'react';
import DishRecipeImageBlock from '../components/DishRecipeImageBlock';
import DishRecipeIngredientsInformation from '../components/DishRecipeIngredientsInformation';
import { useTranslation } from '../../hooks/useTranslation';

const DishRecipePrintLayout = forwardRef(({ dish }, ref) => {
  const { t } = useTranslation();

  if (!dish) {
    return null;
  }

  const description = dish?.description?.trim();

  return (
    <div
      ref={ref}
      className="bg-white text-gray-900"
      style={{
        width: '100%',
        maxWidth: '180mm',
        minHeight: '260mm',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <div className="px-12 py-10">
        <div className="grid grid-cols-[180px_1fr] gap-6 items-start mb-10">
          <div>
            <DishRecipeImageBlock dish={dish} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {dish.name || t('Recept')}
            </h1>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              {t('Recept')}
            </p>
            <div className="text-sm text-gray-600 space-y-1 mb-5">
              {dish.size && dish.unit_abbr && (
                <p>
                  {t('Total mängd')}: {dish.size} {dish.unit_abbr}
                </p>
              )}
              {dish.portion_size && (
                <p>
                  {t('Portionsstorlek')}: {dish.portion_size} g
                </p>
              )}
            </div>
            <div className="text-sm text-gray-800">
              <h2 className="font-semibold text-gray-900 mb-2">
                {t('Måltidsinställningar')}
              </h2>
              <div className="flex flex-wrap gap-2">
                {(dish.meal_types || []).length > 0 ? (
                  dish.meal_types.map((type) => (
                    <span
                      key={`meal-type-${type}`}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-800 text-xs font-medium"
                    >
                      {type}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500">
                    {t('Inga måltidstyper angivna')}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(dish.day_tags || []).length > 0 ? (
                  dish.day_tags.map((day) => (
                    <span
                      key={`day-tag-${day}`}
                      className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 text-green-800 text-xs font-medium"
                    >
                      {day}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-gray-500">
                    {t('Inga veckodagar angivna')}
                  </span>
                )}
              </div>
              {dish.is_dagens_meny && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                    {t('Dagens meny')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <DishRecipeIngredientsInformation dish={dish} />
      </div>

      {description && (
        <section
          className="px-12 py-10"
          style={{
            pageBreakBefore: 'always',
            margin: '0 auto',
            maxWidth: '180mm',
            boxSizing: 'border-box',
          }}
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            {t('Beskrivning')}
          </h2>
          <p className="whitespace-pre-wrap leading-relaxed text-gray-700 text-sm">
            {description}
          </p>
        </section>
      )}
    </div>
  );
});

export default DishRecipePrintLayout;

